// CreatorAccountPanel.tsx — panneau « Mon compte » du créateur (2026-10-29, refonte visuelle)
//
// Ouvert depuis le menu du compte de l'en-tête. Quatre sections en onglets, sous un bandeau
// qui porte l'identité du créateur (avatar, pseudo, crédits) :
//   - Profil           : identité, pseudo public, biographie, page personnelle, adresse, photo.
//                        L'adresse e-mail s'affiche mais ne se modifie pas.
//   - Réseaux sociaux  : Page Facebook et compte Instagram reliés, pour l'annonce « Je suis en
//                        direct » (réglée chaîne par chaîne, onglet Configuration).
//   - Sécurité         : changement de mot de passe (l'ancien est exigé).
//   - Usage            : crédits Kléon, statut du compte, quotas et consommation.
//
// Direction visuelle : la régie (ardoise sombre, bleu #2563EB), un seul parti pris — l'avatar
// entouré d'un anneau dégradé comme un signal de diffusion, et le doré réservé aux crédits.
// Les écritures passent par admin-api CreatorAccountController (liste blanche de champs),
// jamais par AdminUpdate, qui accepte aussi quotas, rôle et statut.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState }        from '../../../store/generalReducer';

import {
  CreatorAccount,
  CreatorAccountUpdate,
  CreatorCredits,
  changeCreatorPassword,
  fetchCreatorAccount,
  fetchCreatorCredits,
  updateCreatorAccount,
} from '../../../backendServices/adminManagment/api_creatorAccount';
import {
  AdminSocialNetworkStatus,
  disconnectSocialNetwork,
  getSocialNetworkAuthorizeUrl,
  linkInstagramViaFacebook,
  listAdminSocialNetworks,
} from '../../../backendServices/adminManagment/api_socialNetworks';
import { checkSlugExistenceAdmin, getAdminPhotoUrl, updateAdminPhoto } from '../../../backendServices/adminManagment/api';
import { generateSlug } from '../../../utils/utils_string';
import SwitchOnOff from '../../widget/inputs/SwitchOnOff';

// ── Constantes ───────────────────────────────────────────────────────────────

type Tab = 'profile' | 'networks' | 'security' | 'usage';

const BIO_MAX      = 2000;
const PASSWORD_MIN = 8;

// ── Icônes (tracés 24×24, trait) ─────────────────────────────────────────────

const PATHS = {
  user    : ['M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z', 'M4.5 20.25a7.5 7.5 0 0115 0'],
  share   : ['M7.2 10.9a2.25 2.25 0 100 2.2m0-2.2c.2.3.3.7.3 1.1s-.1.8-.3 1.1m0-2.2l9.6-5.3m-9.6 7.5l9.6 5.3m0 0a2.25 2.25 0 103.9 2.2 2.25 2.25 0 00-3.9-2.2zm0-12.8a2.25 2.25 0 103.9-2.2 2.25 2.25 0 00-3.9 2.2z'],
  shield  : ['M9 12.75L11.25 15 15 9.75m-3-7.04A12 12 0 013.6 6 12 12 0 003 9.75c0 5.6 3.8 10.3 9 11.6 5.2-1.3 9-6 9-11.6 0-1.3-.2-2.6-.6-3.75h-.15c-3.2 0-6.1-1.25-8.25-3.29z'],
  chart   : ['M3 13.1c0-.6.5-1.1 1.1-1.1h2.3c.6 0 1.1.5 1.1 1.1v6.8c0 .6-.5 1.1-1.1 1.1H4.1A1.1 1.1 0 013 19.9v-6.8zM9.75 8.6c0-.6.5-1.1 1.1-1.1h2.3c.6 0 1.1.5 1.1 1.1v11.3c0 .6-.5 1.1-1.1 1.1h-2.3a1.1 1.1 0 01-1.1-1.1V8.6zM16.5 4.1c0-.6.5-1.1 1.1-1.1h2.3c.6 0 1.1.5 1.1 1.1v15.8c0 .6-.5 1.1-1.1 1.1h-2.3a1.1 1.1 0 01-1.1-1.1V4.1z'],
  eye     : ['M2 12.3C3.4 8.1 7.4 5 12 5s8.6 3.1 10 7.3c.1.2.1.4 0 .6C20.6 17.1 16.6 20 12 20s-8.6-3-10-7.2a.9.9 0 010-.5z', 'M15 12.5a3 3 0 11-6 0 3 3 0 016 0z'],
  eyeOff  : ['M4 8.2A10.5 10.5 0 001.9 12.5C3.2 16.8 7.2 20 12 20c1 0 1.9-.1 2.9-.4M6.2 6.2A10.4 10.4 0 0112 5c4.8 0 8.8 3.2 10.1 7.5a10.5 10.5 0 01-4.3 5.8M6.2 6.2L3 3m3.2 3.2l3.7 3.7m7.9 7.9L21 21m-3.2-3.2l-3.7-3.7m0 0a3 3 0 10-4.2-4.2m4.2 4.2L9.9 9.9'],
  camera  : ['M6.8 6.2A2.3 2.3 0 015.2 7.2c-.4.1-.8.1-1.1.2C3 7.6 2.3 8.5 2.3 9.6V18a2.25 2.25 0 002.2 2.25h15A2.25 2.25 0 0021.8 18V9.6c0-1.1-.8-2-1.8-2.2-.4-.1-.8-.1-1.2-.2a2.3 2.3 0 01-1.6-1l-.8-1.3a2.2 2.2 0 00-1.7-1 49 49 0 00-5.2 0 2.2 2.2 0 00-1.7 1l-.8 1.3z', 'M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z'],
  mic     : ['M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z'],
  check   : ['M4.5 12.75l6 6 9-13.5'],
  x       : ['M6 18L18 6M6 6l12 12'],
  info    : ['M11.25 11.25l.04-.02a.75.75 0 011.06.85l-.7 2.84a.75.75 0 001.06.85l.04-.02M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.01v.01H12V8.25z'],
} as const;

const Ico: React.FC<{ name: keyof typeof PATHS; className?: string }> = ({ name, className = 'w-5 h-5' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"
       className={className} aria-hidden="true">
    {PATHS[name].map((d, i) => <path key={i} d={d} />)}
  </svg>
);

const NAV: ReadonlyArray<{ id: Tab; label: string; title: string; hint: string; icon: keyof typeof PATHS }> = [
  { id: 'profile' , label: 'Profil'          , icon: 'user'  , title: 'Votre profil'      , hint: 'Ce que vos spectateurs voient de vous.' },
  { id: 'networks', label: 'Réseaux sociaux' , icon: 'share' , title: 'Réseaux sociaux'   , hint: 'Annoncez vos directs là où se trouve votre public.' },
  { id: 'security', label: 'Sécurité'        , icon: 'shield', title: 'Sécurité'          , hint: "Protégez l'accès à votre compte." },
  { id: 'usage'   , label: 'Usage'           , icon: 'chart' , title: 'Usage et crédits'  , hint: 'Vos crédits, vos quotas et votre consommation.' },
];

// ── Données du formulaire ────────────────────────────────────────────────────

interface Draft {
  firstname: string; lastname: string; pseudonym: string; biography: string; personalPageUrl: string;
  address: string; zipCode: string; city: string; region: string; country: string;
  // Reporter Kleon : tout en texte comme le reste du brouillon ('1' = sollicitable).
  reporterAvailable: '1' | '0'; reporterCity: string; reporterRadiusKm: string;
}

const draftOf = (a: CreatorAccount): Draft => ({
  firstname: a.firstname ?? '', lastname: a.lastname ?? '', pseudonym: a.pseudonym ?? '',
  biography: a.biography ?? '', personalPageUrl: a.personalPageUrl ?? '',
  address: a.address ?? '', zipCode: a.zipCode ?? '', city: a.city ?? '', region: a.region ?? '', country: a.country ?? '',
  reporterAvailable: a.reporterAvailable ? '1' : '0', reporterCity: a.reporterCity ?? '',
  reporterRadiusKm: a.reporterRadiusKm != null ? String(a.reporterRadiusKm) : '',
});

// 1 Go = 1024³ octets : même convention que les quotas disque (cf. AdminSheet).
const formatBytes = (n: number): string => {
  if (n < 1024 ** 2) return `${Math.round(n / 1024)} Ko`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} Mo`;
  return `${(n / 1024 ** 3).toFixed(2)} Go`;
};

const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
};

const STATUS: Record<string, { label: string; dot: string }> = {
  active   : { label: 'Actif'                   , dot: 'bg-emerald-400' },
  pending  : { label: 'En attente de validation', dot: 'bg-amber-400'   },
  suspended: { label: 'Suspendu'                , dot: 'bg-red-400'     },
  disabled : { label: 'Désactivé'               , dot: 'bg-slate-500'   },
};

// ── Briques d'interface ──────────────────────────────────────────────────────

const inputClass =
  'w-full h-10 rounded-xl bg-slate-950/50 px-3.5 text-sm text-slate-100 ring-1 ring-white/10 placeholder:text-slate-500 '
+ 'transition focus:outline-none focus:ring-2 focus:ring-blue-500/70 disabled:cursor-not-allowed disabled:opacity-60';
const inputInvalid = 'ring-red-500/70 focus:ring-red-500/70';
const btnPrimary =
  'inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 h-10 text-sm font-semibold text-white '
+ 'transition hover:bg-blue-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 '
+ 'focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 disabled:cursor-not-allowed disabled:bg-slate-700 disabled:text-slate-400';
const btnGhost =
  'inline-flex items-center justify-center gap-2 rounded-xl px-3.5 h-10 text-sm font-medium text-slate-200 ring-1 ring-white/10 '
+ 'transition hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 disabled:opacity-50';

const Card: React.FC<{ title?: string; hint?: string; children: React.ReactNode; className?: string }> = ({ title, hint, children, className = '' }) => (
  <section className={`rounded-2xl bg-slate-800/40 p-5 ring-1 ring-white/5 ${className}`}>
    {title && (
      <header className="mb-4">
        <h3 className="text-sm font-semibold text-slate-100">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-slate-400">{hint}</p>}
      </header>
    )}
    {children}
  </section>
);

const Field: React.FC<{ label: string; hint?: React.ReactNode; error?: boolean; children: React.ReactNode }> = ({ label, hint, error, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-medium text-slate-300">{label}</span>
    {children}
    {hint && <span className={`mt-1.5 block text-xs ${error ? 'text-red-400' : 'text-slate-500'}`}>{hint}</span>}
  </label>
);

/** Photo de profil dans son anneau dégradé — la signature du panneau. */
const Avatar: React.FC<{ src: string; initials: string; size: number; verified?: boolean; onBroken?: () => void }> = ({ src, initials, size, verified, onBroken }) => (
  <div className="relative shrink-0" style={{ width: size, height: size }}>
    <div className="h-full w-full rounded-full bg-gradient-to-tr from-blue-500 via-sky-400 to-cyan-200 p-[3px]">
      <div className="flex h-full w-full items-center justify-center overflow-hidden rounded-full bg-slate-900 ring-[3px] ring-slate-900">
        {src
          ? <img src={src} alt="" className="h-full w-full object-cover" onError={onBroken} />
          : <span className="font-semibold tracking-tight text-slate-300" style={{ fontSize: size * 0.32 }}>{initials}</span>}
      </div>
    </div>
    {verified && (
      <span title="Créateur vérifié" className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-white ring-2 ring-slate-900">
        <Ico name="check" className="h-3 w-3" />
      </span>
    )}
  </div>
);

const Meter: React.FC<{ label: string; used: number; max: number; format: (n: number) => string }> = ({ label, used, max, format }) => {
  // Un quota à 0 n'est pas « plein » : c'est « non attribué » ou « sans limite » selon le compte.
  const known = max > 0;
  const pct   = known ? Math.min(100, Math.round((used / max) * 100)) : 0;
  const tone  = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-400' : 'bg-blue-500';
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-3 text-sm">
        <span className="text-slate-300">{label}</span>
        <span className="tabular-nums text-slate-400">
          <span className="font-medium text-slate-100">{format(used)}</span>{known ? ` sur ${format(max)}` : ''}
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-slate-950/60" role="progressbar" aria-label={label}
           aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
        <div className={`h-full rounded-full transition-[width] duration-500 motion-reduce:transition-none ${tone}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const NetworkGlyph: React.FC<{ code: string }> = ({ code }) => code === 'facebook' ? (
  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#1877F2] text-white">
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" aria-hidden="true">
      <path d="M13.5 21v-7.5h2.6l.4-3h-3V8.6c0-.9.3-1.5 1.6-1.5h1.6V4.4c-.3 0-1.2-.1-2.3-.1-2.3 0-3.9 1.4-3.9 4v2.2H7.8v3h2.7V21h3z" />
    </svg>
  </span>
) : (
  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-amber-400 via-rose-500 to-purple-600 text-white">
    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.8} aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="4.5" /><circle cx="12" cy="12" r="3.6" /><circle cx="16.6" cy="7.4" r=".7" fill="currentColor" stroke="none" />
    </svg>
  </span>
);

const NETWORKS: ReadonlyArray<{ code: string; label: string; detail: string }> = [
  { code: 'facebook' , label: 'Facebook' , detail: 'Publication sur une Page que vous administrez.' },
  { code: 'instagram', label: 'Instagram', detail: 'Compte professionnel rattaché à votre Page Facebook.' },
];

// ── Panneau ──────────────────────────────────────────────────────────────────

interface Props {
  adminId : number;
  onClose : () => void;
}

const CreatorAccountPanel: React.FC<Props> = ({ adminId, onClose }) => {
  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);

  const [tab  , setTab  ] = useState<Tab>('profile');
  const [shown, setShown] = useState(false);          // entrée en fondu (coupée si l'utilisateur préfère moins d'animation)

  useEffect(() => {
    const frame = requestAnimationFrame(() => setShown(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  // ── Profil ────────────────────────────────────────────────────────────────
  const [account  , setAccount  ] = useState<CreatorAccount | null>(null);
  const [draft    , setDraft    ] = useState<Draft | null>(null);
  const [loadErr  , setLoadErr  ] = useState('');
  const [saving   , setSaving   ] = useState(false);
  const [saveMsg  , setSaveMsg  ] = useState<{ ok: boolean; text: string } | null>(null);
  const [slugTaken, setSlugTaken] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const load = useCallback(() => {
    fetchCreatorAccount(adminId)
      .then(a => { setAccount(a); setDraft(draftOf(a)); setLoadErr(''); })
      .catch((e: Error) => setLoadErr(e.message));
  }, [adminId]);

  useEffect(() => { load(); }, [load]);

  const slug = useMemo(() => generateSlug(draft?.pseudonym ?? ''), [draft?.pseudonym]);
  const pseudonymChanged = !!account && !!draft && draft.pseudonym.trim() !== (account.pseudonym ?? '');

  // Le pseudo public fait l'adresse de la page (@slug) : on vérifie qu'il est libre avant d'enregistrer.
  useEffect(() => {
    if (!pseudonymChanged || slug === '' || slug === account?.slug) { setSlugTaken(false); return; }
    let cancelled = false;
    checkSlugExistenceAdmin(slug, adminId).then(r => { if (!cancelled) setSlugTaken(r.code === 1 && !!r.payload); });
    return () => { cancelled = true; };
  }, [slug, pseudonymChanged, account?.slug, adminId]);

  const changedFields = useMemo(() => {
    if (!account || !draft) return [] as Array<keyof Draft>;
    const base = draftOf(account);
    return (Object.keys(draft) as Array<keyof Draft>).filter(k => draft[k].trim() !== base[k]);
  }, [account, draft]);

  const dirty = changedFields.length > 0;

  const urlProblem = useMemo(() => {
    const url = draft?.personalPageUrl.trim() ?? '';
    return url !== '' && !/^https?:\/\/\S+\.\S+/i.test(url) ? "L'adresse doit commencer par http:// ou https://" : '';
  }, [draft?.personalPageUrl]);

  const nameProblem = !!draft && (draft.firstname.trim() === '' || draft.lastname.trim() === '');

  // Être reporter exige une ville et un rayon de déplacement raisonnable (1 à 1000 km).
  const reporterRadius  = Number(draft?.reporterRadiusKm ?? '');
  const reporterProblem = !draft || draft.reporterAvailable !== '1' ? ''
    : draft.reporterCity.trim() === '' ? 'Indiquez la ville d\'où vous vous déplacez.'
    : !Number.isInteger(reporterRadius) || reporterRadius < 1 || reporterRadius > 1000 ? 'Indiquez un rayon entre 1 et 1000 km.'
    : '';
  const canSave = dirty && !saving && !slugTaken && urlProblem === '' && !nameProblem && reporterProblem === '';

  const setField = (k: keyof Draft) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setSaveMsg(null);
    setConfirmDiscard(false);
    setDraft(d => (d ? { ...d, [k]: e.target.value } : d));
  };

  const save = async () => {
    if (!account || !draft || !canSave) return;
    setSaving(true);
    setSaveMsg(null);
    const changes: CreatorAccountUpdate = {};
    changedFields.forEach(k => {
      if (k === 'reporterAvailable')      changes.reporterAvailable = draft.reporterAvailable === '1';
      else if (k === 'reporterRadiusKm')  changes.reporterRadiusKm  = draft.reporterRadiusKm.trim() === '' ? null : Number(draft.reporterRadiusKm);
      else                                changes[k] = draft[k].trim();
    });
    // Ville et rayon partent avec l'interrupteur : le serveur valide les trois ensemble.
    if (changes.reporterAvailable !== undefined || changes.reporterCity !== undefined || changes.reporterRadiusKm !== undefined) {
      changes.reporterAvailable = draft.reporterAvailable === '1';
      changes.reporterCity      = draft.reporterCity.trim();
      changes.reporterRadiusKm  = draft.reporterRadiusKm.trim() === '' ? null : Number(draft.reporterRadiusKm);
    }
    // Le slug suit le pseudo : on ne le renvoie que si le pseudo change.
    if (pseudonymChanged) changes.slug = slug;
    try {
      const updated = await updateCreatorAccount(adminId, changes);
      setAccount(updated);
      setDraft(draftOf(updated));
      setSaveMsg({ ok: true, text: 'Modifications enregistrées' });
    } catch (e) {
      setSaveMsg({ ok: false, text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  // Le message de réussite s'efface seul ; une erreur reste jusqu'à la prochaine saisie.
  useEffect(() => {
    if (!saveMsg?.ok) return;
    const timer = window.setTimeout(() => setSaveMsg(null), 3500);
    return () => window.clearTimeout(timer);
  }, [saveMsg]);

  // Fermer avec des changements non enregistrés demande d'abord confirmation (dans la barre du bas).
  const requestClose = useCallback(() => {
    if (saving) return;
    if (dirty) { setConfirmDiscard(true); setTab('profile'); return; }
    onClose();
  }, [saving, dirty, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  // ── Photo ─────────────────────────────────────────────────────────────────
  const fileRef = useRef<HTMLInputElement>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoBusy   , setPhotoBusy   ] = useState(false);
  const [photoErr    , setPhotoErr    ] = useState('');
  const currentPhoto = photoPreview ?? (authentication.admin ? getAdminPhotoUrl(authentication.admin) : '');

  const uploadPhoto = async (file: File | null, remove: boolean) => {
    setPhotoBusy(true);
    setPhotoErr('');
    const result = await updateAdminPhoto(adminId, file, remove);
    setPhotoBusy(false);
    if (result.code !== 1) { setPhotoErr(result.comment || "La photo n'a pas pu être enregistrée."); return; }
    setPhotoPreview(file ? URL.createObjectURL(file) : '');
  };

  useEffect(() => () => { if (photoPreview?.startsWith('blob:')) URL.revokeObjectURL(photoPreview); }, [photoPreview]);

  // ── Réseaux sociaux ───────────────────────────────────────────────────────
  const [networks, setNetworks] = useState<AdminSocialNetworkStatus[] | null>(null);
  const [netErr  , setNetErr  ] = useState('');
  const [netBusy , setNetBusy ] = useState<string | null>(null);

  const reloadNetworks = useCallback(() => {
    listAdminSocialNetworks(adminId).then(setNetworks).catch(() => setNetworks(prev => prev ?? []));
  }, [adminId]);

  useEffect(() => {
    reloadNetworks();
    // Le consentement s'ouvre dans un autre onglet : on relit au retour.
    window.addEventListener('focus', reloadNetworks);
    return () => window.removeEventListener('focus', reloadNetworks);
  }, [reloadNetworks]);

  const networkOf = (code: string) => networks?.find(n => n.code.toLowerCase() === code);

  const runNetwork = async (code: string, action: () => Promise<void>) => {
    setNetBusy(code);
    setNetErr('');
    try { await action(); } catch (e) { setNetErr((e as Error).message); }
    setNetBusy(null);
  };

  const connectNetwork = (code: string) => runNetwork(code, async () => {
    const url = await getSocialNetworkAuthorizeUrl(adminId, code);
    window.open(url, '_blank', 'noopener,noreferrer');
  });
  const disconnectNetwork = (code: string, id: number) => runNetwork(code, async () => {
    await disconnectSocialNetwork(adminId, id);
    reloadNetworks();
  });
  const linkInstagram = () => runNetwork('instagram', async () => {
    await linkInstagramViaFacebook(adminId);
    reloadNetworks();
  });

  // ── Mot de passe ──────────────────────────────────────────────────────────
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew    , setPwdNew    ] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdReveal , setPwdReveal ] = useState(false);
  const [pwdBusy   , setPwdBusy   ] = useState(false);
  const [pwdMsg    , setPwdMsg    ] = useState<{ ok: boolean; text: string } | null>(null);

  const rules = [
    { ok: pwdNew.length >= PASSWORD_MIN                    , label: `${PASSWORD_MIN} caractères au minimum` },
    { ok: pwdNew !== '' && pwdNew !== pwdCurrent           , label: "Différent du mot de passe actuel" },
    { ok: pwdNew !== '' && pwdConfirm === pwdNew           , label: 'Les deux saisies correspondent' },
  ];
  const strength = pwdNew.length < PASSWORD_MIN ? 0
    : 1 + Number(pwdNew.length >= 12) + Number(/[a-z]/.test(pwdNew) && /[A-Z]/.test(pwdNew)) + Number(/\d/.test(pwdNew) || /[^A-Za-z0-9]/.test(pwdNew));
  const STRENGTH_LABELS = ['Trop court', 'Faible', 'Correct', 'Bon', 'Excellent'];
  const STRENGTH_TONES  = ['bg-slate-600', 'bg-red-500', 'bg-amber-400', 'bg-emerald-400', 'bg-emerald-400'];
  const canChangePwd = pwdCurrent !== '' && rules.every(r => r.ok) && !pwdBusy;

  const changePassword = async () => {
    if (!canChangePwd) return;
    setPwdBusy(true);
    setPwdMsg(null);
    try {
      await changeCreatorPassword(adminId, pwdCurrent, pwdNew);
      setPwdCurrent(''); setPwdNew(''); setPwdConfirm('');
      setPwdMsg({ ok: true, text: 'Mot de passe modifié. Utilisez-le à votre prochaine connexion.' });
    } catch (e) {
      setPwdMsg({ ok: false, text: (e as Error).message });
    }
    setPwdBusy(false);
  };

  // ── Crédits Kléon ─────────────────────────────────────────────────────────
  const [credits, setCredits] = useState<CreatorCredits | null>(null);
  useEffect(() => {
    fetchCreatorCredits(adminId).then(setCredits).catch(() => { /* la section reste sans chiffre */ });
  }, [adminId]);

  // ── Rendu ─────────────────────────────────────────────────────────────────
  const current = NAV.find(n => n.id === tab)!;
  const displayName = [draft?.firstname, draft?.lastname].filter(Boolean).join(' ') || account?.email || '';
  const initials = `${(draft?.firstname ?? '')[0] ?? ''}${(draft?.lastname ?? '')[0] ?? ''}`.toUpperCase();
  const status = account ? (STATUS[account.accountStatus] ?? { label: account.accountStatus, dot: 'bg-slate-500' }) : null;
  const creditsLabel = credits ? credits.credits.toLocaleString('fr-FR') : '—';

  // Statut de reporter ENREGISTRÉ (pas le brouillon en cours de saisie).
  const reporterOn = !!account && account.reporterAvailable && !!account.reporterCity && !!account.reporterRadiusKm;

  // La pastille mène à la carte « Reporter Kleon » de l'onglet Profil.
  const goToReporter = () => {
    setTab('profile');
    window.setTimeout(() => {
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      document.getElementById('account-reporter-card')?.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    }, 60);
  };

  const profileTab = draft && account && (
    <div className="space-y-4">
      <Card>
        <div className="flex items-center gap-5">
          <div className="group relative">
            <Avatar src={currentPhoto} initials={initials} size={88} verified={account.verified} onBroken={() => setPhotoPreview('')} />
            <button type="button" aria-label="Changer la photo" disabled={photoBusy} onClick={() => fileRef.current?.click()}
                    className="absolute inset-0 flex items-center justify-center rounded-full bg-slate-950/60 text-white opacity-0 transition focus-visible:opacity-100 group-hover:opacity-100 disabled:cursor-wait">
              <Ico name="camera" className="h-6 w-6" />
            </button>
          </div>
          <div className="min-w-0">
            <p className="truncate text-base font-semibold text-slate-100">{displayName}</p>
            <p className="truncate text-sm text-slate-400">{slug ? `@${slug}` : account.email}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" className={btnGhost} disabled={photoBusy} onClick={() => fileRef.current?.click()}>
                {photoBusy ? 'Envoi…' : currentPhoto ? 'Changer la photo' : 'Ajouter une photo'}
              </button>
              {currentPhoto && <button type="button" className={btnGhost} disabled={photoBusy} onClick={() => uploadPhoto(null, true)}>Retirer</button>}
            </div>
            {photoErr && <p role="alert" className="mt-2 text-xs text-red-400">{photoErr}</p>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
                 onChange={e => { const f = e.target.files?.[0]; if (f) uploadPhoto(f, false); e.target.value = ''; }} />
        </div>
      </Card>

      <Card title="Identité" hint="Votre nom et votre pseudo public.">
        <div className="space-y-4">
          <Field label="Adresse e-mail" hint="Elle identifie votre compte et ne peut pas être modifiée ici.">
            <input className={inputClass} value={account.email} disabled readOnly />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Prénom"><input className={inputClass} value={draft.firstname} onChange={setField('firstname')} maxLength={100} autoComplete="given-name" /></Field>
            <Field label="Nom"><input className={inputClass} value={draft.lastname} onChange={setField('lastname')} maxLength={100} autoComplete="family-name" /></Field>
          </div>
          <Field label="Pseudo public" error={slugTaken}
                 hint={slugTaken ? 'Ce pseudo est déjà pris.' : slug ? `Votre page publique : @${slug}` : 'Le nom affiché avec vos chaînes et vos vidéos.'}>
            <input className={`${inputClass} ${slugTaken ? inputInvalid : ''}`} value={draft.pseudonym} onChange={setField('pseudonym')} maxLength={100} />
          </Field>
        </div>
      </Card>

      <Card title="Présentation" hint="Visible sur votre page publique.">
        <div className="space-y-4">
          <Field label="Biographie" hint={<span className="tabular-nums">{draft.biography.length} / {BIO_MAX}</span>}>
            <textarea className={`${inputClass} h-auto min-h-[104px] resize-y py-2.5`} value={draft.biography} onChange={setField('biography')} maxLength={BIO_MAX} />
          </Field>
          <Field label="Page personnelle" error={urlProblem !== ''} hint={urlProblem || 'Votre site ou votre page web. Facultatif.'}>
            <input className={`${inputClass} ${urlProblem ? inputInvalid : ''}`} value={draft.personalPageUrl} onChange={setField('personalPageUrl')}
                   placeholder="https://" inputMode="url" maxLength={255} />
          </Field>
        </div>
      </Card>

      <div id="account-reporter-card">
      <Card title="Reporter Kleon" hint="Acceptez d'être sollicité pour couvrir un événement près de chez vous.">
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p id="reporter-switch-label" className="text-sm font-medium text-slate-100">Je suis disponible comme reporter</p>
              <p className="text-xs text-slate-400">Kleon pourra vous proposer des événements à couvrir.</p>
            </div>
            <SwitchOnOff checked={draft.reporterAvailable === '1'} labelledBy="reporter-switch-label"
                    onChange={() => { setSaveMsg(null); setConfirmDiscard(false); setDraft(d => (d ? { ...d, reporterAvailable: d.reporterAvailable === '1' ? '0' : '1' } : d)); }} />
          </div>

          {draft.reporterAvailable === '1' && (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <Field label="Ville de départ" error={reporterProblem !== '' && draft.reporterCity.trim() === ''}>
                    <input className={inputClass} value={draft.reporterCity} onChange={setField('reporterCity')} maxLength={255} placeholder="Lyon" autoComplete="address-level2" />
                  </Field>
                </div>
                <Field label="Rayon (km)" error={reporterProblem !== '' && draft.reporterCity.trim() !== ''}>
                  <input className={inputClass} value={draft.reporterRadiusKm} inputMode="numeric" placeholder="50"
                         onChange={e => { setSaveMsg(null); setConfirmDiscard(false); const v = e.target.value.replace(/\D/g, '').slice(0, 4); setDraft(d => (d ? { ...d, reporterRadiusKm: v } : d)); }} />
                </Field>
              </div>
              <p className={`text-xs ${reporterProblem ? 'text-red-400' : 'text-slate-400'}`}>
                {reporterProblem || `Vous serez sollicité pour des événements dans un rayon de ${draft.reporterRadiusKm} km autour de ${draft.reporterCity.trim()}.`}
              </p>
            </>
          )}
        </div>
      </Card>
      </div>

      <Card title="Adresse postale" hint="Utilisée pour votre facturation. Elle n'est jamais affichée publiquement.">
        <div className="space-y-4">
          <Field label="Adresse"><input className={inputClass} value={draft.address} onChange={setField('address')} maxLength={255} autoComplete="street-address" /></Field>
          <div className="grid gap-4 sm:grid-cols-3">
            <Field label="Code postal"><input className={inputClass} value={draft.zipCode} onChange={setField('zipCode')} maxLength={20} autoComplete="postal-code" /></Field>
            <div className="sm:col-span-2"><Field label="Ville"><input className={inputClass} value={draft.city} onChange={setField('city')} maxLength={100} autoComplete="address-level2" /></Field></div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Région"><input className={inputClass} value={draft.region} onChange={setField('region')} maxLength={100} autoComplete="address-level1" /></Field>
            <Field label="Pays"><input className={inputClass} value={draft.country} onChange={setField('country')} maxLength={100} autoComplete="country-name" /></Field>
          </div>
        </div>
      </Card>
    </div>
  );

  const networksTab = (
    <div className="space-y-4">
      <div className="flex gap-3 rounded-2xl bg-blue-500/10 p-4 text-sm text-blue-100 ring-1 ring-blue-400/20">
        <Ico name="info" className="mt-0.5 h-5 w-5 shrink-0 text-blue-300" />
        <p>
          Quand une de vos chaînes passe à l'antenne, une carte « en direct » est publiée sur les comptes reliés.
          Le texte et les réseaux se règlent chaîne par chaîne : onglet Configuration, section « Annonce sur les réseaux ».
        </p>
      </div>

      {NETWORKS.map(({ code, label, detail }) => {
        const n = networkOf(code);
        const connected = !!n?.connected;
        const busy = netBusy === code;
        const facebookConnected = !!networkOf('facebook')?.connected;
        return (
          <Card key={code}>
            <div className="flex flex-wrap items-center gap-4">
              <NetworkGlyph code={code} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-slate-100">{label}</p>
                <p className="text-xs text-slate-400">{detail}</p>
                <p className="mt-1.5 flex items-center gap-1.5 text-xs">
                  <span className={`h-1.5 w-1.5 rounded-full ${n === undefined ? 'bg-slate-600' : connected ? 'bg-emerald-400' : 'bg-slate-500'}`} />
                  <span className={connected ? 'text-emerald-300' : 'text-slate-400'}>
                    {networks === null ? 'Vérification…'
                      : n === undefined ? 'Indisponible'
                      : connected ? `Relié${n.externalAccountLabel ? ` · ${n.externalAccountLabel}` : ''}`
                      : n.implemented ? 'Non relié' : 'Bientôt disponible'}
                  </span>
                </p>
              </div>
              {n !== undefined && (
                <div className="flex flex-wrap gap-2">
                  {connected ? (
                    <button type="button" className={btnGhost} disabled={busy}
                            onClick={() => n.adminSocialNetworkId !== null && disconnectNetwork(code, n.adminSocialNetworkId)}>
                      {busy ? '…' : 'Délier'}
                    </button>
                  ) : (
                    <>
                      {code === 'instagram' && facebookConnected && (
                        <button type="button" className={btnGhost} disabled={busy} onClick={linkInstagram}>Utiliser ma Page Facebook</button>
                      )}
                      <button type="button" className={btnPrimary} disabled={busy || !n.implemented} onClick={() => connectNetwork(code)}>
                        {busy ? 'Ouverture…' : `Relier ${label}`}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </Card>
        );
      })}

      {netErr && <p role="alert" className="rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-300 ring-1 ring-red-400/20">{netErr}</p>}
    </div>
  );

  const securityTab = (
    <form className="space-y-4" onSubmit={e => { e.preventDefault(); changePassword(); }}>
      <Card title="Changer le mot de passe" hint="Pour votre sécurité, votre mot de passe actuel est demandé.">
        <div className="space-y-4">
          <Field label="Mot de passe actuel">
            <input className={inputClass} type={pwdReveal ? 'text' : 'password'} autoComplete="current-password" value={pwdCurrent}
                   onChange={e => { setPwdCurrent(e.target.value); setPwdMsg(null); }} />
          </Field>
          <Field label="Nouveau mot de passe">
            <input className={inputClass} type={pwdReveal ? 'text' : 'password'} autoComplete="new-password" value={pwdNew}
                   onChange={e => { setPwdNew(e.target.value); setPwdMsg(null); }} />
          </Field>
          <Field label="Confirmer le nouveau mot de passe">
            <input className={inputClass} type={pwdReveal ? 'text' : 'password'} autoComplete="new-password" value={pwdConfirm}
                   onChange={e => { setPwdConfirm(e.target.value); setPwdMsg(null); }} />
          </Field>

          <button type="button" className="inline-flex items-center gap-2 text-xs font-medium text-slate-400 transition hover:text-slate-200"
                  aria-pressed={pwdReveal} onClick={() => setPwdReveal(v => !v)}>
            <Ico name={pwdReveal ? 'eyeOff' : 'eye'} className="h-4 w-4" />{pwdReveal ? 'Masquer les saisies' : 'Afficher les saisies'}
          </button>

          {pwdNew !== '' && (
            <div aria-live="polite">
              <div className="flex gap-1.5">
                {[1, 2, 3, 4].map(i => (
                  <span key={i} className={`h-1 flex-1 rounded-full transition-colors ${strength >= i ? STRENGTH_TONES[strength] : 'bg-slate-700'}`} />
                ))}
              </div>
              <p className="mt-1.5 text-xs text-slate-400">Robustesse : <span className="font-medium text-slate-200">{STRENGTH_LABELS[strength]}</span></p>
            </div>
          )}

          <ul className="space-y-1.5">
            {rules.map(r => (
              <li key={r.label} className={`flex items-center gap-2 text-xs ${r.ok ? 'text-emerald-300' : 'text-slate-500'}`}>
                <Ico name={r.ok ? 'check' : 'x'} className="h-3.5 w-3.5" />{r.label}
              </li>
            ))}
          </ul>
        </div>
      </Card>

      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" className={btnPrimary} disabled={!canChangePwd}>{pwdBusy ? 'Modification…' : 'Changer le mot de passe'}</button>
        {pwdMsg && <p role={pwdMsg.ok ? 'status' : 'alert'} className={`text-sm ${pwdMsg.ok ? 'text-emerald-300' : 'text-red-300'}`}>{pwdMsg.text}</p>}
      </div>
    </form>
  );

  const usageTab = account && (
    <div className="space-y-4">
      <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-400/15 via-slate-800/40 to-slate-800/40 p-6 ring-1 ring-amber-300/20">
        <p className="text-xs font-medium uppercase tracking-[0.14em] text-amber-200/80">Crédits Kléon</p>
        <p className="mt-2 flex items-baseline gap-2">
          <span className="text-5xl font-semibold tabular-nums tracking-tight text-amber-200">{creditsLabel}</span>
          <span aria-hidden className="text-2xl text-amber-300">✦</span>
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl bg-slate-950/40 p-4">
            <p className="text-xs text-slate-400">Directs terminés</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-slate-100">+{credits ? credits.broadcastCredits.toLocaleString('fr-FR') : '—'}</p>
            <p className="text-xs text-slate-500">{credits ? `${formatDuration(credits.broadcastSeconds)} diffusées` : ' '}</p>
          </div>
          <div className="rounded-xl bg-slate-950/40 p-4">
            <p className="text-xs text-slate-400">Likes sur vos vidéos</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-slate-100">+{credits ? credits.likeCredits.toLocaleString('fr-FR') : '—'}</p>
            <p className="text-xs text-slate-500">{credits ? `${credits.likes.toLocaleString('fr-FR')} like${credits.likes > 1 ? 's' : ''}` : ' '}</p>
          </div>
          <div className="rounded-xl bg-slate-950/40 p-4">
            <p className="text-xs text-slate-400">Vidéos et directs regardés</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-slate-100">+{credits ? (credits.viewingCredits ?? 0).toLocaleString('fr-FR') : '—'}</p>
            <p className="text-xs text-slate-500">{credits ? `${formatDuration(credits.viewingSeconds ?? 0)} regardées, 1 crédit par 10 minutes` : ' '}</p>
          </div>
        </div>
        <p className="mt-4 text-xs text-slate-400">
          {credits
            ? `${credits.rates.creditsPerBlock} crédits par tranche de ${Math.round(credits.rates.blockSeconds / 60)} minutes de direct terminée, et ${credits.rates.creditsPerLike} crédits par like.`
            : 'Les crédits augmentent avec vos directs et les likes de vos vidéos.'}
        </p>
      </section>

      <Card title="Mon compte">
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
          <div><dt className="text-xs text-slate-400">Statut</dt><dd className="mt-1 flex items-center gap-2 text-sm text-slate-100"><span className={`h-2 w-2 rounded-full ${status?.dot}`} />{status?.label}</dd></div>
          <div><dt className="text-xs text-slate-400">Créateur vérifié</dt><dd className="mt-1 text-sm text-slate-100">{account.verified ? 'Oui' : 'Non'}</dd></div>
          <div><dt className="text-xs text-slate-400">Compte créé le</dt><dd className="mt-1 text-sm text-slate-100">{account.creationDate ? new Date(account.creationDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}</dd></div>
          <div><dt className="text-xs text-slate-400">Page publique</dt><dd className="mt-1 text-sm text-slate-100">{account.slug ? `@${account.slug}` : '—'}</dd></div>
        </dl>
      </Card>

      <Card title="Quotas" hint="Pour les augmenter, contactez l'équipe Kléon.">
        <div className="space-y-5">
          <Meter label="Stockage"  used={account.quotaUsed}      max={account.quotaDisk}     format={formatBytes} />
          <Meter label="Chaînes"   used={account.nbChannelsUsed} max={account.nbChannelsMax} format={n => `${n}`} />
          <Meter label="Appareils" used={account.nbDevicesUsed}  max={account.nbDevicesMax}  format={n => `${n}`} />
          <Meter label="Durée de diffusion"     used={account.durationElapsedBroadcasting} max={account.durationMaxBroadcasting} format={formatDuration} />
          <Meter label="Durée d'enregistrement" used={account.durationElapsedRecording}    max={account.durationMaxRecording}    format={formatDuration} />
        </div>
      </Card>
    </div>
  );

  const body = loadErr
    ? <p role="alert" className="rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-300 ring-1 ring-red-400/20">Impossible de charger votre compte : {loadErr}</p>
    : !account || !draft
      ? <div className="space-y-4" aria-busy="true">{[0, 1, 2].map(i => <div key={i} className="h-32 animate-pulse rounded-2xl bg-slate-800/40 motion-reduce:animate-none" />)}</div>
      : tab === 'profile' ? profileTab : tab === 'networks' ? networksTab : tab === 'security' ? securityTab : usageTab;

  // Flèches gauche / droite : passent d'un onglet à l'autre (motif WAI-ARIA des onglets).
  const onTabsKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'Home' && e.key !== 'End') return;
    e.preventDefault();
    const index = NAV.findIndex(n => n.id === tab);
    const next  = e.key === 'Home' ? 0 : e.key === 'End' ? NAV.length - 1
                : (index + (e.key === 'ArrowRight' ? 1 : -1) + NAV.length) % NAV.length;
    setTab(NAV[next].id);
    document.getElementById(`account-tab-${NAV[next].id}`)?.focus();
  };

  return createPortal(
    <div className={`fixed inset-0 z-[10000] flex items-end justify-center bg-slate-950/70 backdrop-blur-sm transition-opacity duration-200 motion-reduce:transition-none md:items-center md:p-6 ${shown ? 'opacity-100' : 'opacity-0'}`}
         onMouseDown={e => { if (e.target === e.currentTarget) requestClose(); }}>
      <div role="dialog" aria-modal="true" aria-label="Mon compte"
           className={`flex h-[100dvh] w-full flex-col overflow-hidden bg-slate-900 text-slate-100 shadow-2xl ring-1 ring-white/10 transition duration-200 motion-reduce:transition-none md:h-[680px] md:max-h-[92vh] md:max-w-3xl md:rounded-3xl ${shown ? 'translate-y-0 scale-100' : 'translate-y-3 scale-[0.98]'}`}>

        {/* Bandeau : identité, crédits, fermeture */}
        <header className="relative shrink-0 bg-gradient-to-b from-blue-500/10 to-transparent px-5 pt-5 md:px-7 md:pt-6">
          <div className="flex items-center gap-4">
            <Avatar src={currentPhoto} initials={initials} size={60} verified={account?.verified} onBroken={() => setPhotoPreview('')} />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">Mon compte</p>
              <p className="truncate text-lg font-semibold leading-tight tracking-tight text-slate-50">{displayName || ' '}</p>
              <p className="truncate text-sm text-slate-400">{account?.slug ? `@${account.slug}` : account?.email}</p>

              {/* Pastilles d'état : crédits et statut de reporter, côte à côte */}
              <div className="mt-2.5 flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => setTab('usage')} title="Voir le détail des crédits"
                        className="inline-flex items-center gap-1.5 rounded-full bg-amber-300/10 px-3 py-1 text-xs font-medium text-amber-200 ring-1 ring-amber-300/20 transition hover:bg-amber-300/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300/60">
                  <span aria-hidden>✦</span><span className="tabular-nums">{creditsLabel}</span> crédits
                </button>
                {account && (
                  <button type="button" onClick={goToReporter}
                          title={reporterOn ? 'Modifier ma disponibilité de reporter' : 'Devenir reporter Kleon'}
                          className={`inline-flex max-w-full items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ring-1 transition focus-visible:outline-none focus-visible:ring-2 ${reporterOn
                            ? 'bg-emerald-400/10 text-emerald-200 ring-emerald-300/25 hover:bg-emerald-400/15 focus-visible:ring-emerald-300/60'
                            : 'text-slate-400 ring-white/10 hover:bg-white/5 hover:text-slate-200 focus-visible:ring-blue-400'}`}>
                    <Ico name="mic" className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">
                      {reporterOn
                        ? <>Reporter<span className="text-emerald-200/60"> · </span>{account.reporterCity}<span className="text-emerald-200/60"> · </span><span className="tabular-nums">{account.reporterRadiusKm} km</span></>
                        : 'Reporter : non'}
                    </span>
                    {reporterOn && <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-400" />}
                  </button>
                )}
              </div>
            </div>
            {account?.lastLoginDate && (
              <div className="shrink-0 self-start pt-1 text-right text-xs leading-snug text-slate-400">
                <p>Dernière connexion : <span className="text-slate-200">
                  {new Date(account.lastLoginDate).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                </span></p>
                {(account.lastLoginCity || account.lastLoginCountry) && (
                  <p>{[account.lastLoginCity, account.lastLoginCountry].filter(Boolean).join(', ')}</p>
                )}
              </div>
            )}
            <button type="button" aria-label="Fermer" onClick={requestClose}
                    className="flex h-9 w-9 shrink-0 items-center justify-center self-start rounded-full text-slate-400 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400">
              <Ico name="x" className="h-5 w-5" />
            </button>
          </div>

          {/* Onglets */}
          <div role="tablist" aria-label="Sections du compte" onKeyDown={onTabsKeyDown}
               className="-mx-5 mt-5 flex gap-1 overflow-x-auto border-b border-white/10 px-5 md:-mx-7 md:px-7">
            {NAV.map(item => {
              const active = tab === item.id;
              return (
                <button key={item.id} id={`account-tab-${item.id}`} type="button" role="tab" aria-selected={active}
                        aria-controls="account-tabpanel" tabIndex={active ? 0 : -1} onClick={() => setTab(item.id)}
                        className={`-mb-px flex shrink-0 items-center gap-2 border-b-2 px-3.5 pb-3 pt-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400 ${active ? 'border-blue-400 text-white' : 'border-transparent text-slate-400 hover:text-slate-200'}`}>
                  <Ico name={item.icon} className={`h-[18px] w-[18px] ${active ? 'text-blue-300' : ''}`} />{item.label}
                </button>
              );
            })}
          </div>
        </header>

        {/* Contenu de l'onglet */}
        <div id="account-tabpanel" role="tabpanel" aria-labelledby={`account-tab-${tab}`} className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 pt-5 md:px-7">
          <div className="mb-5 flex items-start justify-between gap-4">
            <p className="text-sm text-slate-400">{current.hint}</p>
            {saveMsg?.ok && <span role="status" className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-emerald-300"><Ico name="check" className="h-4 w-4" />{saveMsg.text}</span>}
          </div>
          {body}
        </div>

        {/* Barre d'enregistrement : n'occupe de place que s'il y a des modifications */}
        <div className={`grid shrink-0 transition-[grid-template-rows] duration-200 motion-reduce:transition-none ${dirty || saveMsg?.ok === false ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
          <div className="overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/5 bg-slate-900/95 px-5 py-3 md:px-7">
              {confirmDiscard ? (
                <>
                  <p className="text-sm text-amber-200">Fermer sans enregistrer vos modifications ?</p>
                  <div className="flex gap-2">
                    <button type="button" className={btnGhost} onClick={() => setConfirmDiscard(false)}>Continuer la saisie</button>
                    <button type="button" className={btnPrimary} onClick={onClose}>Abandonner</button>
                  </div>
                </>
              ) : (
                <>
                  <p className={`text-sm ${saveMsg?.ok === false ? 'text-red-300' : 'text-slate-300'}`} role={saveMsg?.ok === false ? 'alert' : undefined}>
                    {saveMsg?.ok === false ? saveMsg.text : `${changedFields.length} modification${changedFields.length > 1 ? 's' : ''} non enregistrée${changedFields.length > 1 ? 's' : ''}`}
                  </p>
                  <div className="flex gap-2">
                    <button type="button" className={btnGhost} disabled={saving || !account} onClick={() => { if (account) { setDraft(draftOf(account)); setSaveMsg(null); } }}>Annuler</button>
                    <button type="button" className={btnPrimary} disabled={!canSave} onClick={save}>{saving ? 'Enregistrement…' : 'Enregistrer les modifications'}</button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default CreatorAccountPanel;
